import { useState } from "react"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Field, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { postPublicJSON } from "@/lib/api"

export function WaitlistForm({ footer }: { footer?: React.ReactNode }) {
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const onSubmit = async () => {
    setLoading(true)
    setError(null)

    try {
      await postPublicJSON("/v1/waitlist", { email, name, reason })
      setSuccess(true)
    } catch (err: any) {
      setError(err?.message || "Failed to submit")
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <>
        <CardHeader>
          <CardTitle>You're on the waitlist!</CardTitle>
          <CardDescription>We'll be in touch soon via email.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <a
            href="https://yomimono.ai"
            className={buttonVariants({
              variant: "ghost",
              className: "w-full",
            })}
          >
            Go back
          </a>
        </CardContent>
      </>
    )
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Join the waitlist</CardTitle>
        <CardDescription>
          Request access to Yomimono and we'll notify you when spots open up.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="email-field">Email</FieldLabel>
          <Input
            id="email-field"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="name-field">Name</FieldLabel>
          <Input
            id="name-field"
            type="text"
            placeholder="Your name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="reason-field">Say hi!</FieldLabel>
          <Textarea
            id="reason-field"
            rows={4}
            placeholder="Tell us how you found us and why you're interested in Yomimono…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          onClick={onSubmit}
          disabled={loading || !(email && name && reason)}
          className="w-full"
        >
          {loading ? "Sending…" : "Request access"}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          By joining, you agree we may email you about Yomimono. See our{" "}
          <a
            href="https://yomimono.ai/privacy.html"
            className="underline hover:text-foreground"
          >
            Privacy Policy
          </a>
          .
        </p>

        {footer}
      </CardContent>
    </>
  )
}
